<template>
  <div class="planCard" :class="{small: isSmall, [name]: true}">
    <div class="planCardInner">
      <div class="d-flex align-center ga-2 mb-5">
        <p class="name">{{ name }}</p>
        <div v-if="popular" class="popularBadge">Most popular</div>
      </div>

      <p v-if="!!monthlyPrice && !!yearlyPrice" class="price mb-5">$
        <template v-if="type === 'monthly'">{{ monthlyPrice }}</template>
        <template v-else>{{ yearlyPrice }}</template>
        <span class="priceInfo"> / {{ type === 'monthly' ? 'month' : 'year' }} / user</span>
      </p>
      <p v-else class="price mb-5">Custom Pricing</p>

      <p class="description">{{ description }}</p>

      <v-btn
        size="large"
        variant="flat"
        class="w-100 dark-button mb-8"
        color="var(--dark-5, #1C1F1D)"
        @click="$emit('onSelect')"
        v-if="!isPreview"
      >
        <template v-if="!!monthlyPrice && !!yearlyPrice">
          <template v-if="name === 'Basic'">
            Purchase Now
          </template>
          <template v-else>
            <span v-if="!!activeSubscription?.lastSubscriptionDetails">
              Purchase Now
            </span>
            <span v-else>
              Start 4 Week Free Trial
            </span>
          </template>
        </template>
        <template v-else>
          Contact Us
        </template>
      </v-btn>

      <div class="divider"/>

      <p class="includes">
        <template v-if="!!monthlyPrice && !!yearlyPrice">
          <template v-if="name === 'Basic'">
            This includes:
          </template>
          <template v-else>
            Everything in Basic, plus:
          </template>
        </template>
        <template v-else>
          Everything in Pro, plus:
        </template>
      </p>

      <ul class="features">
        <li v-for="feature in features.slice(0, isPreview ? 3 : undefined)" :key="feature" class="featureItem">
          <v-icon size="large" color="#1C1F1D">mds:check</v-icon>
          <span class="featureItemLabel">{{ feature }}</span>
        </li>
        <li class="seeAllFeatures" v-if="isPreview" @click="openPricingPage">
          See all features
        </li>
      </ul>

      <div class="mt-auto" v-if="isSmall && currentPlanName">
        <div class="d-flex justify-end mt-8">
          <v-btn
            size="large"
            variant="flat"
            class="dark-button"
            color="var(--dark-5, #1C1F1D)"
            @click="$emit('onSelect')"
          >
            <template v-if="name === 'Enterprise'">
              Contact Us
            </template>
            <template v-else>
              <template v-if="currentPlanName !== name">
                Change to
              </template>
              <template v-else>
                Keep using
              </template>
              {{ name }}
            </template>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { PLANS, ENTERPRISE_PLAN } from '@/utils/payments'

export default {
  name: 'PlanCard',
  props: {
    name: String,
    users: String,
    description: String,
    monthlyPrice: Number,
    yearlyPrice: Number,
    type: String,
    popular: Boolean,
    isSmall: Boolean,
    isPreview: Boolean,
    currentPlanName: String
  },
  computed: {
    ...mapState({
      activeSubscription: state => state.payments.activeSubscription
    }),
    features: function () {
      if (this.name === 'Enterprise') {
        return ENTERPRISE_PLAN.features
      } else {
        const plan = PLANS.find(p => p.name === this.name)
        if (plan) {
          return plan.features
        }
      }
      return []
    }
  },
  methods: {
    openPricingPage () {
      window.open('https://www.qlerify.com/pricing')
    }
  }
}
</script>

<style scoped>
.planCard {
  width: 100%;
  flex-grow: 1;
  flex-shrink: 0;
  max-width: 443px;

  &.Basic {
    .planCardInner {
      border: 2px solid var(--plan-card-basic, #3BB0E6);
    }
  }

  &.Pro {
    .planCardInner {
      border: 2px solid var(--plan-card-pro, #A0F090);
    }
  }

  &.Enterprise {
    .planCardInner {
      border: 2px solid var(--plan-card-enterprise, #A091FF);
    }
  }

  &.small {
    padding-bottom: 0;
    max-width: 356px;

    .planCardInner {
      height: 100%;
      display: flex;
      border-radius: 8px;
      min-height: initial;
      flex-direction: column;
      background: var(--light-1, #FCFCFC);
    }

    .price {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 32px;
    }

    .includes {
      font-size: 12px;
      margin-bottom: 8px;
    }

    .features {
      gap: 8px;
    }

    .featureItem {
      gap: 8px;
    }

    .featureItemLabel {
      font-size: 12px;
    }

  }
}

.popularBadge {
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 400;
  margin-left: 8px;
  line-height: 18.2px;
  border-radius: 3px;
  display: inline-block;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
  background-color: var(--plan-card-pro, #A0F090);
}

.planCardInner {
  width: 100%;
  padding: 24px;
  min-height: 734px;
  border-radius: 20px;
  background-color: var(--white, #FFF);
}

.name {
  font-size: 32px;
  font-weight: 500;
  line-height: 38.4px;
  letter-spacing: -0.03em;
  font-family: Faktum, sans-serif;
}

.price {
  font-size: 40px;
  font-weight: 400;
  line-height: 48px;
  letter-spacing: -0.03em;
  color: var(--dark-5, #1C1F1D);
  font-family: Faktum, sans-serif;
}

.priceInfo {
  font-size: 12px;
  font-weight: 400;
  color: #1C1F1DCC;
  line-height: 15px;
  letter-spacing: -0.03em;
  font-family: Inter, sans-serif;
}

.description {
  font-size: 16px;
  font-weight: 400;
  min-height: 52px;
  line-height: 25.6px;
  margin-bottom: 32px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

:global(.v-btn.v-btn--size-large.v-btn--variant-flat) {
  &.dark-button {
    font-size: 16px;
    font-weight: 500;
    line-height: 19.2px;
    letter-spacing: -0.02em;
    font-family: Faktum, sans-serif;
    border-radius: 58px !important;
    background-color: var(--dark-5, #1C1F1D) !important;
  }
}

.divider {
  width: 100%;
  height: 1px;
  margin-bottom: 24px;
  background-color: var(--light-5, #C8CCCA)
}

.includes {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 24px;
  line-height: 25.6px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

.features {
  gap: 16px;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  list-style-type: none;
}

.featureItem {
  gap: 12px;
  display: flex;
  align-items: center;
}

.featureItemLabel {
  font-size: 16px;
  font-weight: 400;
  font-family: Inter;
  line-height: 25.6px;
  letter-spacing: -0.02em;
  color: var(--dark-5, #1C1F1D);
}

.seeAllFeatures {
  font-size: 12px;
  cursor: pointer;
  line-height: 15px;
  font-weight: 400;
  padding-left: 32.5px;
  color: var(--blue-3, #4285F4);
}
</style>
